import { lazy, Suspense, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import type { HostInfo, Me } from '@buildiary/core';
import { AppContext, LangProvider, useLang, useMedia, useT, type AppState } from './app/context';
import { HostContext, OTHER_HOST, useHost } from './app/host';
import { installNativeLinks, nativeNotify, setNativeLang } from './lib/native';
import { landingFor } from './app/nav';
import { Guard, Shell } from './app/Shell';
import { ApiFailure, request } from './lib/api';
import { Loading, ToastProvider } from './ui/kit';
import { SignIn } from './pages/SignIn';
import { ClientLinkPage, SupplierLinkPage } from './pages/Public';
import { SetPasswordPage } from './pages/SetPassword';
import { StartPage, VerifyLinkPage } from './pages/Start';
import { FindPage, MyHomePage, NoWorkspacePage, PublicFeedbackPage } from './pages/MyHome';

const Home = lazy(() => import('./pages/Home'));
const Approvals = lazy(() => import('./pages/Approvals'));
const Stores = lazy(() => import('./pages/Stores'));
const Receive = lazy(() => import('./pages/Receive'));
const Money = lazy(() => import('./pages/Money'));
const Office = lazy(() => import('./pages/Office'));
const Gate = lazy(() => import('./pages/Gate'));
const Scan = lazy(() => import('./pages/Scan'));
const Projects = lazy(() => import('./pages/Projects'));
const Diary = lazy(() => import('./pages/Diary'));
const Purchasing = lazy(() => import('./pages/Purchasing'));
const Clients = lazy(() => import('./pages/Clients'));
const Reports = lazy(() => import('./pages/Reports'));
const Settings = lazy(() => import('./pages/Settings'));
const Phone = lazy(() => import('./pages/Phone'));

installNativeLinks();

export default function App() {
  return (
    <LangProvider>
      <ToastProvider>
        <BrowserRouter>
          <HostGate />
        </BrowserRouter>
      </ToastProvider>
    </LangProvider>
  );
}

/** Which address this is (a workspace, my.buildiary.app, the demo, …) decides what the app shows. */
function HostGate() {
  const [host, setHost] = useState<HostInfo | null>(null);
  const { lang } = useLang();
  useEffect(() => {
    request('GET /api/host').then(setHost, () => setHost(OTHER_HOST));
  }, []);
  useEffect(() => {
    setNativeLang(lang);
    nativeNotify('ready');
  }, [lang]);
  if (!host) return <div className="boot" aria-busy="true" />;
  return (
    <HostContext.Provider value={host}>
      <Root />
    </HostContext.Provider>
  );
}

function Root() {
  const host = useHost();
  if (host.kind === 'my')
    return (
      <Routes>
        <Route path="/start" element={<StartPage />} />
        <Route path="/start/verify" element={<VerifyLinkPage />} />
        <Route path="/find" element={<FindPage />} />
        <Route path="/feedback" element={<PublicFeedbackPage />} />
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="*" element={<MyHomePage />} />
      </Routes>
    );
  if ((host.kind === 'workspace' || host.kind === 'demo') && !host.known) return <NoWorkspacePage />;
  return (
    <Routes>
      <Route path="/c/:token" element={<ClientLinkPage />} />
      <Route path="/s/:token" element={<SupplierLinkPage />} />
      <Route path="/set-password" element={<SetPasswordPage />} />
      {host.kind === 'other' && <Route path="/start" element={<StartPage />} />}
      {host.kind === 'other' && <Route path="/start/verify" element={<VerifyLinkPage />} />}
      <Route path="/find" element={<FindPage />} />
      <Route path="*" element={<Session />} />
    </Routes>
  );
}

function Session() {
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [failed, setFailed] = useState<string | null>(null);
  const { lang, setLang } = useLang();
  const t = useT();
  const load = useCallback(() => {
    setFailed(null);
    request('GET /api/me').then(
      (m) => {
        setMe(m);
        if (m.user.lang !== lang) setLang(m.user.lang);
      },
      (e: unknown) => {
        if (e instanceof ApiFailure && (e.status === 401 || e.status === 403)) setMe(null);
        else if (e instanceof ApiFailure && e.status === 404) setMe(null);
        else setFailed(e instanceof Error ? e.message : String(e));
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(load, [load]);
  if (failed)
    return (
      <div className="boot">
        <div className="empty">
          <h3>{t('c.error')}</h3>
          <p>No connection to the Buildiary server. Check the internet and try again.</p>
          <button type="button" className="btn primary" onClick={load}>{t('c.retry')}</button>
        </div>
      </div>
    );
  if (me === undefined) return <div className="boot">{t('c.loading')}</div>;
  if (me === null)
    return (
      <SignIn
        onSignedIn={(m) => {
          setMe(m);
          // the language picked on the sign-in page wins, and is saved to the profile
          if (m.user.lang !== lang) request('POST /api/me/lang', { body: { lang } }).then(setMe, () => undefined);
        }}
      />
    );
  return <AppProvider me={me} setMe={setMe} />;
}

function AppProvider({ me, setMe }: { me: Me; setMe: (m: Me | null) => void }) {
  const isPhone = useMedia('(max-width: 767px)');
  const [version, setVersion] = useState(0);
  const [approvals, setApprovals] = useState(0);
  const nav = useNavigate();
  const loc = useLocation();
  const changed = useCallback(() => setVersion((v) => v + 1), []);
  useEffect(() => {
    let live = true;
    if (!me.caps.approve) {
      setApprovals(0);
      return;
    }
    request('GET /api/approvals').then((a) => live && setApprovals(a.length), () => undefined);
    return () => {
      live = false;
    };
  }, [me.caps.approve, version, loc.pathname]);
  const project = me.projects.find((p) => p.id === me.projectId) ?? me.projects[0] ?? null;
  const state = useMemo<AppState>(
    () => ({
      me,
      caps: me.caps,
      project,
      projectId: project?.id,
      currency: project?.currency ?? me.workspace.currency,
      setMe: (m) => setMe(m),
      setProject: async (id) => {
        const m = await request('POST /api/me/project', { body: { projectId: id } });
        setMe(m);
        setVersion((v) => v + 1);
      },
      signOut: async () => {
        try {
          await request('POST /api/auth/logout');
        } catch {
          /* signed out anyway */
        }
        nativeNotify('signout');
        nav('/', { replace: true });
        setMe(null);
      },
      version,
      changed,
      approvals,
      isPhone,
    }),
    [me, project, version, changed, approvals, isPhone, setMe, nav],
  );
  return (
    <AppContext.Provider value={state}>
      <AppRoutes landing={landingFor(me.role, isPhone)} />
    </AppContext.Provider>
  );
}

function Page({ children }: { children: ReactNode }) {
  return (
    <Guard>
      <Suspense fallback={<Loading rows={6} />}>{children}</Suspense>
    </Guard>
  );
}

function AppRoutes({ landing }: { landing: string }) {
  return (
    <Routes>
      <Route path="/gate" element={<Page><Gate /></Page>} />
      <Route path="/scan" element={<Page><Scan /></Page>} />
      <Route element={<Shell />}>
        <Route index element={<Navigate to={landing} replace />} />
        <Route path="/signin" element={<Navigate to={landing} replace />} />
        <Route path="/sign-in" element={<Navigate to={landing} replace />} />
        <Route path="/feedback" element={<Navigate to={`${landing}?feedback=1`} replace />} />
        <Route path="/approvals" element={<Page><Approvals /></Page>} />
        <Route path="/home/*" element={<Page><Home /></Page>} />
        <Route path="/m/owner" element={<Page><Phone view="owner" /></Page>} />
        <Route path="/projects/diary" element={<Page><Diary /></Page>} />
        <Route path="/projects/*" element={<Page><Projects /></Page>} />
        <Route path="/office/*" element={<Page><Office /></Page>} />
        <Route path="/stores/receive" element={<Page><Receive /></Page>} />
        <Route path="/stores/*" element={<Page><Stores /></Page>} />
        <Route path="/buy/*" element={<Page><Purchasing /></Page>} />
        <Route path="/money/*" element={<Page><Money /></Page>} />
        <Route path="/clients/*" element={<Page><Clients /></Page>} />
        <Route path="/reports/*" element={<Page><Reports /></Page>} />
        <Route path="/settings/*" element={<Page><Settings /></Page>} />
        <Route path="*" element={<NotFound landing={landing} />} />
      </Route>
    </Routes>
  );
}

function NotFound({ landing }: { landing: string }) {
  const t = useT();
  const nav = useNavigate();
  return (
    <div className="empty" style={{ paddingTop: 80 }}>
      <h3>{t('c.notFound')}</h3>
      <button type="button" className="btn primary" onClick={() => nav(landing)}>{t('c.goHome')}</button>
    </div>
  );
}
